import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, Signal, signal, WritableSignal, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FocusShapeType } from '@app/_enums/focus-tracker.enum';
import { SettingsService } from '@app/_services/settings.service';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { generateShapeSequence, generateOptions, countShapes, pickQuestionShape, getDurationForLevel } from './focus-game-logic';
import { Subscription, interval } from 'rxjs';
import { FocusGameState } from '@app/_types/focus-tracker.types';
import { FocusSession, FocusSetup } from '@app/_interfaces/focus-tracker.interface';
import { MatMenuModule } from '@angular/material/menu';
import { SecondsToTimePipe } from '@app/_pipes/seconds-to-time.pipe';
import { FocusStore } from '@app/_stores';
import { getNewId } from '@app/_utils/utils';
import { AlertService } from '@app/_services/alert.service';
import { ModalService } from '@app/_services/modal.service';
import { FirestoreCollection } from '@app/_enums/firestore-collection.enum';
import { StorageService } from '@app/_services/storage.servive';
import { CacheService } from '@app/_services/cache.service';
import { log } from '@app/_services/logger';
import { FocusShapeComponent } from '../focus-shape/focus-shape.component';

@Component({
  selector: 'app-focus-game',
  imports: [
    CommonModule,
    SvgComponent,
    MatMenuModule,
    SecondsToTimePipe,
    FocusShapeComponent,
  ],
  templateUrl: './focus-game.component.html',
  styleUrl: './focus-game.component.scss'
})

export class FocusGameComponent implements OnInit, OnDestroy {

  readonly focusStore = inject(FocusStore);
  readonly FocusShapeType = FocusShapeType;

  // Game state
  currentState: WritableSignal<FocusGameState> = signal({ type: 'home', currentLevel: 1, isFirstTime: true });

  // Game internals
  private timerSubscription: Subscription | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private shapeSequence: { shape: FocusShapeType; showDuration: number; hideDuration: number }[] = [];
  private shapeCounts: Record<string, number> = {};
  private currentShapeIndex = 0;
  private shapePhaseSecondsLeft = 0;
  private isShowingShape = true;
  private gameSeed = 0;

  // Session tracking — one session per game (matches Flutter)
  private sessionId: string | null = null;
  private sessionStartedAt: Date | null = null;
  private highestLevelReached = 1;
  private failures: Record<string, number> = {};

  selectedSetupId: WritableSignal<string> = signal('');

  homeSetup: Signal<FocusSetup | null> = computed(() => {
    const setups = this.focusStore.enabledSetups();
    const sid = this.selectedSetupId();
    if (sid) {
      return setups.find(s => s.id === sid) || (setups.length > 0 ? setups[0] : null);
    }
    return setups.length > 0 ? setups[0] : null;
  });

  /**
   * Starting level for the home screen. Always 1 — the user always starts
   * from Level 1 when opening the game. Continuous progression within a
   * session is tracked via [state.level] (in GameResult / GamePlaying), not
   * by the home signal. Closing / quitting resets back to Level 1.
   */
  userCurrentLevel: Signal<number> = computed(() => 1);

  /** Home screen always shows the first-time layout (rules + disclaimer). */
  isFirstTime: Signal<boolean> = computed(() => true);

  /** Max level screen is never shown — home always resets to Level 1. */
  isMaxLevel: Signal<boolean> = computed(() => false);

  /** Timer display: remaining seconds formatted */
  remainingDisplay: Signal<number> = computed(() => {
    const state = this.currentState();
    if (state.type === 'playing') return state.remaining;
    return 0;
  });

  /** Progress percentage for timer bar */
  timerProgress: Signal<number> = computed(() => {
    const state = this.currentState();
    if (state.type === 'playing') {
      return ((state.totalSeconds - state.remaining) / state.totalSeconds) * 100;
    }
    return 0;
  });

  constructor(
    public ss: SettingsService,
    public router: Router,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private storageService: StorageService,
    public cc: CacheService,
    private modalService: ModalService,
  ) {}

  ngOnInit() {
    const setupId = this.route.snapshot.queryParamMap.get('setupId');
    if (setupId) {
      this.selectedSetupId.set(setupId);
    }
    // Initialize home state with current level
    this.updateHomeState();
  }

  ngOnDestroy() {
    this.stopTimer();
    this.clearCountdown();
  }

  private updateHomeState() {
    this.currentState.set({
      type: 'home',
      currentLevel: this.userCurrentLevel(),
      isFirstTime: this.isFirstTime()
    });
  }

  // === Navigation ===

  goHome() {
    this.stopTimer();
    this.clearCountdown();
    this.resetSession();
    this.updateHomeState();
  }

  async closeGame(): Promise<void> {
    const state = this.currentState();

    if (state.type === 'playing' || state.type === 'countdown') {
      this.stopTimer();
      const confirm = await this.alertService.confirmRevamped({
        title: this.ss.t('exitFocusTracker_title'),
        description: this.ss.t('exitFocusTracker_content'),
        yesButtonText: this.ss.t('exitFocusTracker_exit'),
        noButtonText: this.ss.t('common_resume'),
        yesButtonColor: 'color-11',
        noButtonColor: 'color-35',
      });
      if (confirm === true) {
        // Save partial session and exit
        if (this.sessionId) {
          await this.saveSession();
        }
        this.resetSession();
        this.router.navigate(['/today']);
      } else {
        // Resume
        if (state.type === 'playing') {
          this.timerSubscription = interval(1000).subscribe(() => this.onTimerTick());
        }
      }
      return;
    }

    this.router.navigate(['/today']);
  }

  // === Home state actions ===

  startLevel() {
    const level = this.userCurrentLevel() + 1;
    this.startCountdown(level);
  }

  async openEditSetup() {
    const { FocusSetupFormComponent } = await import('../focus-setup-form/focus-setup-form.component');
    const setup = this.homeSetup();
    if (!setup) return;
    this.modalService.openLevel1(
      FocusSetupFormComponent,
      { mode: 'edit', value: setup },
      { disableClose: true, autoFocus: false }
    );
  }

  openMessage() {
    const setup = this.homeSetup();
    if (!setup?.members) return;
    const setupId = `ec_${setup.id}`;
    import('@app/components/addons/message/message-view/message-view.component').then(({ MessageViewComponent }) => {
      this.modalService.openLevel1(
        MessageViewComponent,
        { setupId, entityId: setup.id, entityCollection: FirestoreCollection.FocusSetups },
        { disableClose: true, autoFocus: false }
      );
    });
  }

  openFeatureInfo() {
    import('@app/components/common/feature-guides/feature-guide-dialog/feature-guide-dialog.component').then(({ FeatureGuideDialogComponent }) => {
      this.modalService.openLevel1(
        FeatureGuideDialogComponent,
        { entityType: 'focus' },
        { disableClose: true, autoFocus: false }
      );
    });
  }

  // === Countdown ===

  private startCountdown(level: number) {
    this.currentState.set({ type: 'countdown', level, countdownValue: 3 });
    this.countdownInterval = setInterval(() => {
      const state = this.currentState();
      if (state.type !== 'countdown') return;
      if (state.countdownValue > 1) {
        this.currentState.set({ ...state, countdownValue: state.countdownValue - 1 });
      } else {
        this.clearCountdown();
        this.startGame(level);
      }
    }, 1000);
  }

  private clearCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  // === Game ===

  private startGame(level: number) {
    this.stopTimer();
    this.gameSeed = Date.now();

    // Create session on first level start (one session per game)
    if (!this.sessionId) {
      this.sessionId = `focus_${getNewId()}`;
      this.sessionStartedAt = new Date();
      this.highestLevelReached = 1;
      this.failures = {};
      log.info('FocusGame', 'Session started', { sessionId: this.sessionId, level });
    }

    this.shapeSequence = generateShapeSequence(level, this.gameSeed);
    this.shapeCounts = countShapes(this.shapeSequence);
    this.currentShapeIndex = 0;
    this.isShowingShape = true;

    const totalSeconds = getDurationForLevel(level);

    if (this.shapeSequence.length > 0) {
      this.shapePhaseSecondsLeft = this.shapeSequence[0].showDuration;
      this.currentState.set({
        type: 'playing',
        level,
        currentShape: this.shapeSequence[0].shape,
        remaining: totalSeconds,
        totalSeconds
      });
    }

    this.timerSubscription = interval(1000).subscribe(() => this.onTimerTick());
    log.debug('FocusGame', 'Game started', { level, shapes: this.shapeSequence.length, totalSeconds });
  }

  private onTimerTick() {
    const state = this.currentState();
    if (state.type !== 'playing') return;

    const newRemaining = state.remaining - 1;
    this.shapePhaseSecondsLeft--;

    // Timer ran out — go to answer phase
    if (newRemaining <= 0) {
      this.enterAnswerPhase(state.level);
      return;
    }

    if (this.shapePhaseSecondsLeft <= 0) {
      if (this.isShowingShape) {
        // Switch to hide phase
        this.isShowingShape = false;
        this.shapePhaseSecondsLeft = this.shapeSequence[this.currentShapeIndex]?.hideDuration ?? 2;
        this.currentState.set({
          ...state,
          currentShape: null,
          remaining: newRemaining
        });
      } else {
        // Show next shape
        this.currentShapeIndex++;
        this.isShowingShape = true;
        if (this.currentShapeIndex < this.shapeSequence.length) {
          this.shapePhaseSecondsLeft = this.shapeSequence[this.currentShapeIndex].showDuration;
          this.currentState.set({
            ...state,
            currentShape: this.shapeSequence[this.currentShapeIndex].shape,
            remaining: newRemaining
          });
        } else {
          // All shapes shown — go to answer
          this.enterAnswerPhase(state.level);
          return;
        }
      }
    } else {
      this.currentState.set({ ...state, remaining: newRemaining });
    }
  }

  private enterAnswerPhase(level: number) {
    this.stopTimer();
    const { shape, correctCount } = pickQuestionShape(this.shapeCounts, this.gameSeed + 1);
    const options = generateOptions(correctCount);
    this.currentState.set({
      type: 'answer',
      level,
      questionShape: shape,
      correctCount,
      options,
      selectedAnswer: null
    });
  }

  selectAnswer(option: number) {
    const state = this.currentState();
    if (state.type !== 'answer') return;
    this.currentState.set({
      ...state,
      selectedAnswer: state.selectedAnswer === option ? null : option
    });
  }

  async submitAnswer() {
    const state = this.currentState();
    if (state.type !== 'answer' || state.selectedAnswer === null) return;

    const isCorrect = state.selectedAnswer === state.correctCount;
    this.currentState.set({
      type: 'result',
      level: state.level,
      correctCount: state.correctCount,
      userAnswer: state.selectedAnswer,
      isCorrect
    });

    if (isCorrect) {
      this.highestLevelReached = state.level;
    } else {
      const levelKey = state.level.toString();
      this.failures[levelKey] = (this.failures[levelKey] ?? 0) + 1;
    }

    await this.saveSession();
  }

  tryAgain() {
    const state = this.currentState();
    if (state.type !== 'result') return;
    this.startCountdown(state.level);
  }

  nextLevel() {
    this.resetSession();
    this.updateHomeState();
  }

  crackNextLevel() {
    const state = this.currentState();
    if (state.type !== 'result') return;
    this.startCountdown(state.level + 1);
  }

  private async saveSession() {
    if (!this.sessionId || !this.sessionStartedAt) return;

    const targetSetupId = this.selectedSetupId() || this.focusStore.enabledSetups()[0]?.id;
    if (!targetSetupId) return;

    const session: FocusSession = {
      id: this.sessionId,
      uid: this.cc.user.uid,
      startedAt: this.sessionStartedAt.toISOString(),
      updatedAt: new Date().toISOString(),
      levelReached: Math.min(this.highestLevelReached, 10),
      failures: Object.keys(this.failures).length > 0 ? { ...this.failures } : undefined,
    };

    await this.focusStore.addOrUpdateSession(targetSetupId, session);
    log.info('FocusGame', 'Session saved', { levelReached: this.highestLevelReached, sessionId: this.sessionId });
  }

  private resetSession() {
    this.sessionId = null;
    this.sessionStartedAt = null;
    this.highestLevelReached = 1;
    this.failures = {};
  }

  // === Timer management ===

  private stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }
}
